import React, { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import { FaQrcode, FaTimes } from 'react-icons/fa';

const QRScanner = ({ onScan, onClose, placeholder = "Skaneeri QR-kood" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    // Set the worker path for qr-scanner
    QrScanner.WORKER_PATH = '/qr-scanner-worker.min.js';
    
    return () => {
      // Cleanup scanner when component unmounts
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      }
    };
  }, []);

  // Start scanning when modal opens and video element is available
  useEffect(() => {
    if (isLoading) {
      console.log('Modal is loading, waiting for video element...');
      // Give React time to render the video element
      const timeout = setTimeout(() => {
        if (videoRef.current) {
          console.log('Video element found after timeout, starting scanner...');
          startScanning();
        } else {
          console.error('Video element still not available after timeout');
          setError('Video element ei ole saadaval');
          setIsLoading(false);
        }
      }, 100); // Small delay to allow DOM to update

      return () => clearTimeout(timeout);
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScan = (result) => {
    if (result && result.data) {
      try {
        console.log('QR Code scanned:', result.data);
        onScan(result.data);
        stopScanning();
      } catch (err) {
        console.error('Error processing QR scan:', err);
        setError('QR-koodi lugemine nurjus');
      }
    }
  };

  const openModal = () => {
    console.log('Opening QR scanner modal...');
    setIsLoading(true);
    setError('');
  };

  const startScanning = async () => {
    console.log('Starting QR scanner...');
    console.log('Setting loading state...');
    setError('');
    console.log('Loading state already set');
    
    try {
      console.log('Checking mediaDevices API...');
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.log('MediaDevices API not available');
        setError('Kaamera API ei ole selles brauseris toetatud');
        setIsLoading(false);
        return;
      }
      console.log('MediaDevices API is available');

      console.log('Checking HTTPS...');
      // Check if HTTPS is required (camera needs secure context)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        console.log('HTTPS required but not available');
        setError('Kaamera kasutamiseks on vaja HTTPS ühendust. Palun külastage lehte https:// kaudu.');
        setIsLoading(false);
        return;
      }
      console.log('HTTPS check passed');

      console.log('Checking camera availability...');
      // Check if camera is available
      const hasCamera = await QrScanner.hasCamera();
      console.log('Camera available:', hasCamera);
      
      if (!hasCamera) {
        setError('Kaamera ei ole saadaval selles seadmes');
        setIsLoading(false);
        return;
      }

      console.log('Requesting camera permissions...');
      // Request camera permissions explicitly
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment', // Prefer back camera
            width: { ideal: 640 },
            height: { ideal: 480 }
          } 
        });
        console.log('Camera permission granted, stream:', stream);
        
        // Stop the stream as QrScanner will create its own
        stream.getTracks().forEach(track => track.stop());
      } catch (permissionError) {
        console.error('Camera permission denied:', permissionError);
        setError(`Kaamera viga: ${permissionError.name} - ${permissionError.message}`);
        setIsLoading(false);
        return;
      }

      console.log('Creating QR scanner instance...');
      // Create QR scanner instance
      scannerRef.current = new QrScanner(
        videoRef.current,
        handleScan,
        {
          onDecodeError: (err) => {
            // Don't show decode errors as they happen frequently during scanning
            console.debug('QR decode error:', err);
          },
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment', // Use back camera on mobile
          maxScansPerSecond: 5, // Throttle scanning for better performance
        }
      );

      console.log('Starting QR scanner...');
      // Start scanning
      await scannerRef.current.start();
      console.log('QR Scanner started successfully');
      
      // Set scanning state after successful start
      setIsLoading(false);
      setIsScanning(true);
      
      // Check if video is playing
      const video = videoRef.current;
      if (video) {
        video.addEventListener('loadedmetadata', () => {
          console.log('Video metadata loaded, dimensions:', video.videoWidth, 'x', video.videoHeight);
        });
        
        video.addEventListener('playing', () => {
          console.log('Video is now playing');
        });
        
        video.addEventListener('error', (e) => {
          console.error('Video error:', e);
          setError('Video taasesitamisel tekkis viga');
        });
      }
    } catch (err) {
      console.error('CATCH BLOCK: Failed to start QR scanner:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      if (err.name === 'NotAllowedError') {
        setError('Kaamera kasutamiseks on vaja õiguseid. Palun lubage kaamera kasutamine brauseris.');
      } else if (err.name === 'NotFoundError') {
        setError('Kaamera ei ole saadaval selles seadmes.');
      } else if (err.name === 'NotSupportedError') {
        setError('Kaamera ei ole selles brauseris toetatud.');
      } else {
        setError('Kaamera käivitamisel tekkis viga: ' + (err.message || 'Tundmatu viga'));
      }
      setIsLoading(false);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    setIsScanning(false);
    setIsLoading(false);
    setError('');
    
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    
    if (onClose) onClose();
  };

  return (
    <div className="qr-scanner-container">
      {!isLoading && !isScanning ? (
        <button
          type="button"
          onClick={openModal}
          className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-md bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          <FaQrcode className="mr-2" />
          {placeholder}
        </button>
      ) : (
        <div className="qr-scanner-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isLoading ? 'Kaamera käivitamine...' : 'QR-koodi skaneerimine'}
              </h3>
              <button
                onClick={stopScanning}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {error ? (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            ) : (
              <div className="mb-4">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative">
                  {/* Video element - always present in DOM */}
                  <video
                    ref={videoRef}
                    className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                    style={{ width: '100%', height: '100%' }}
                    autoPlay
                    muted
                    playsInline
                  />
                  
                  {/* Loading overlay */}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Kaameraga ühenduse loomine...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="text-sm text-gray-600 text-center">
              {isLoading 
                ? 'Palun lubage kaamera kasutamine kui brauseriaknas küsitakse' 
                : 'Suunake kaamera QR-koodile lugeja aktiveerimiseks'
              }
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={stopScanning}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Tühista
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner; 